import { useContext, useEffect } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import {
  AlertBadge,
  GoToPublicsDocs,
  LayoutContext,
  Tabs,
  TutorialDialog,
} from '@/_shared/components';
import { TutorialKeys } from '@/_shared/constants';
import { useTutorialCheck } from '@/_shared/hooks';
import { useBreakingPoints } from '@/_shared/hooks/useBreakpoint';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useTabs } from '@/_shared/hooks/useTabs';
import { getDepartmentTeamsCall } from '@/departments/state/api/getDepartmentTeamsCall';
import { getEmployeesMeCall } from '@/profile/state/api';
import { DepartmentAssessment, TabDepartment } from '../components';

export const DepartmentScreen = () => {
  const t = useTranslations('department');
  const tShared = useTranslations();
  const { openDialog: openDialogManagerTutorial } = useDialog('managerDepartmentTutorial');

  const { setLayout } = useContext(LayoutContext);
  const { isDesktop } = useBreakingPoints();

  const { hasAlreadySeenTutorial } = useTutorialCheck();

  const {
    success: successGetEmployeesMe,
    error: errorGetEmployeesMe,
    leadership,
  } = getEmployeesMeCall.hook();

  const departmentId = leadership?.department?.id;

  const {
    execute: executeGetDepartmentTeams,
    loading: loadingGetDepartmentTeams,
    success: successGetDepartmentTeams,
    error: errorGetDepartmentTeams,
    response: responseGetDepartmentTeams,
  } = getDepartmentTeamsCall.hook();

  const tabsInitialState = [
    {
      title: t('tabs.overview'),
      index: 0,
      active: true,
      dataTestId: 'department',
    },
    {
      title: t('tabs.assessment'),
      index: 1,
      active: false,
      dataTestId: 'assessment',
    },
  ];
  const { tabs, setOpenTab } = useTabs({
    tabsInitialState,
  });

  const slides = [
    {
      title: t('tutorial.department.slides.slide1.title'),
      description: t('tutorial.department.slides.slide1.description'),
      assetName: 'tutorialLearningDepartmentSlide1',
    },
    {
      title: t('tutorial.department.slides.slide2.title'),
      description: t('tutorial.department.slides.slide2.description'),
      assetName: 'tutorialLearningDepartmentSlide2',
    },
    {
      title: t('tutorial.department.slides.slide3.title'),
      description: t('tutorial.department.slides.slide3.description'),
      assetName: 'tutorialLearningDepartmentSlide3',
    },
    {
      title: t('tutorial.department.slides.slide4.title'),
      description: t('tutorial.department.slides.slide4.description'),
      assetName: 'tutorialLearningDepartmentSlide4',
    },
  ];

  useEffect(() => {
    setLayout({ title: 'department' });
  }, [setLayout, t]);

  useEffect(() => {
    if (departmentId) {
      executeGetDepartmentTeams({ departmentId });
    }
  }, [departmentId, executeGetDepartmentTeams]);

  useEffect(() => {
    if (!hasAlreadySeenTutorial(TutorialKeys.ManagerDepartment)) {
      openDialogManagerTutorial();
    }
  }, [hasAlreadySeenTutorial, openDialogManagerTutorial]);

  if ((!departmentId && successGetEmployeesMe) || errorGetEmployeesMe) {
    const message = tShared('errors.genericApiError');
    return <AlertBadge containerProps={{ mt: 's' }} iconName='info' type='error' label={message} />;
  }

  return (
    <Box>
      <Box>
        <Box display='flex' justifyContent='flex-end'>
          <GoToPublicsDocs />
        </Box>
        <Box overflowX={isDesktop ? 'auto' : 'scroll'}>
          <Tabs buttonVariant='primary' setActiveTab={setOpenTab} tabs={tabs} />
        </Box>
        {tabs.map(tab => {
          return (
            <Box key={tab.title} display='flex' flexDirection='column'>
              {tab.index === 0 && tab.active && (
                <>
                  <Box mt='s'>
                    <AlertBadge iconName='info' type='primary' label={t('dataMismatchWarning')} />
                  </Box>
                  <TabDepartment
                    departmentId={departmentId}
                    loading={loadingGetDepartmentTeams}
                    error={errorGetDepartmentTeams}
                    success={successGetDepartmentTeams}
                    responseGetDepartmentTeams={responseGetDepartmentTeams}
                  />
                </>
              )}
              {tab.index === 1 && tab.active && (
                <DepartmentAssessment
                  totalEmployees={responseGetDepartmentTeams?.department.stats?.totalEmployees}
                  department={responseGetDepartmentTeams}
                />
              )}
            </Box>
          );
        })}
      </Box>
      <TutorialDialog
        translationsKey='department.tutorial.department'
        dialogKey='managerDepartmentTutorial'
        slides={slides}
      />
    </Box>
  );
};
